/**
 * @hookum/protocol — wire message schemas (client ↔ server).
 *
 * Single source of truth for all WebSocket message types. Uses zod for
 * runtime validation on both client and server. Intentionally decoupled from
 * @hookum/engine — shapes are duplicated here as an independent wire contract
 * per ARCHITECTURE §2.1.
 */

import { z } from "zod";

/** Bumped 1 → 2 in v1.2 (Phase 12) when the explicit `pass` move re-entered
 * the wire protocol alongside the v1.1 timer-based auction. Clients that
 * negotiated v1 will still parse v2 server messages (passes are additive),
 * but a v1-only server will reject the new `{ t: "pass" }` client message
 * at the zod parse layer.
 *
 * v1.2 Phase 14 (round-end / game-end Continue gate, 2026-05-21) is
 * intentionally additive at the wire layer: a new `{ t: "continueRound" }`
 * client message + three optional fields on `RedactedView`
 * (`gateKind`, `roundAcks`, `youAcked`). PROTOCOL_VERSION is NOT bumped —
 * the new client message is rejected by a v2-only server (cosmetic dead
 * gate), the new RedactedView fields default to absent on v2 servers and
 * the client treats `gateKind` undefined/null identically. Phase 14
 * therefore stays a no-break upgrade.
 *
 * Bumped 2 → 3 in v1.4 (Phase 16) when smart-bot info hygiene lifted two
 * new fields onto `RedactedView`: `publicHistory` (every completed trick
 * this round + its winner seat) and `openingBid` (the round's opening
 * contract). Mirror field `bidding.openingBid` is added to
 * `PublicBidding` for backward-compat read access. All three are
 * defaulted (`[]` / `null` / `null`) so a v3 client that connects to a
 * v2 server still parses cleanly; the v3 server always emits them. The
 * LastTrickPeek UI is also removed in Phase 16, but that's a client-only
 * deletion and does NOT affect the wire schema.
 *
 * Bumped 3 → 4 in v1.4.1 (Phase 21.2, 2026-05-22) — formal recognition of
 * the Phase 20 (bidding skip) wire surface additions that landed in v1.4
 * without the bump:
 *   - `PublicBidding.highBidderReady: boolean` (default `false`) — surfaces
 *     the new synchronous early-end consent half.
 *   - `{ t: "toggleReady" }` client message + `{ type: "toggleReady" }`
 *     `WireMove` / `Move` variant + matching `GameEvent`
 *     `highBidderReadyChanged`.
 * All additions stay zod-parse-additive (defaults / discriminated-union
 * extensions), but a v3-only server rejects `{ t: "toggleReady" }` at the
 * parse layer — that is a silent desync from the user's perspective. Phase
 * 21.2 also adds an OPTIONAL `protocolVersion?: number` field to
 * `JoinMsg` and a server handshake check: if the client sends a
 * `protocolVersion` that does not equal the server's `PROTOCOL_VERSION`,
 * the server responds with `{ t: "error", code: "PROTOCOL_MISMATCH",
 * message: "App is out of date — refresh" }` and closes the connection.
 * For one phase soak, missing `protocolVersion` (undefined) is tolerated
 * so the Cloudflare Pages (web) and PartyKit (server) deploys can roll
 * over independently; the optional sentinel becomes required in a
 * follow-up phase (Phase 22.5+).
 *
 * Versioning convention for future bumps: every additive wire change MUST
 * bump this number in the same PR that lands the schema delta. The
 * companion protocol-version test (`PROTOCOL_VERSION`) is the loud
 * regression guard. */
export const PROTOCOL_VERSION = 4;

// ---------------------------------------------------------------------------
// Core data shapes (mirror engine types — independent wire contract)
// ---------------------------------------------------------------------------

export const SuitSchema = z.enum(["C", "D", "H", "S"]);
export type Suit = z.infer<typeof SuitSchema>;

export const RankSchema = z.union([
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
  z.literal(8),
  z.literal(9),
  z.literal(10),
  z.literal(11),
  z.literal(12),
  z.literal(13),
  z.literal(14),
]);
export type Rank = z.infer<typeof RankSchema>;

export const JokerColorSchema = z.enum(["RED", "BLACK"]);
export type JokerColor = z.infer<typeof JokerColorSchema>;

/** Discriminated-union card: pip card or joker. */
export const CardSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("pip"),
    suit: SuitSchema,
    rank: RankSchema,
    id: z.string(),
  }),
  z.object({
    kind: z.literal("joker"),
    color: JokerColorSchema,
    id: z.string(),
  }),
]);
export type Card = z.infer<typeof CardSchema>;

export const TeamSchema = z.enum(["A", "B"]);
export type Team = z.infer<typeof TeamSchema>;

export const PlayerCountSchema = z.union([z.literal(4), z.literal(6)]);
export type PlayerCount = z.infer<typeof PlayerCountSchema>;

export const PhaseSchema = z.enum([
  "bidding",
  "playing",
  "round_end",
  "game_over",
]);
export type Phase = z.infer<typeof PhaseSchema>;

/** v1.2 (RULES §2 + §9 A3 + §9.2 Phase 13, 2026-05-21): 6-player is jokers-ON
 * only. The 6p-jokers-OFF mode (48 cards, all four 2s removed, base 5, T = 8)
 * is removed from the engine + protocol + lobby. `.refine()` enforces the
 * invariant at the wire layer so any client / server / test that tries to
 * negotiate `{players: 6, jokers: false}` fails the zod parse with a clear
 * message rather than reaching the engine's `INVALID_CONFIG` throw. */
export const GameConfigSchema = z
  .object({
    players: PlayerCountSchema,
    jokers: z.boolean(),
  })
  .refine((c) => !(c.players === 6 && !c.jokers), {
    message:
      "INVALID_CONFIG: 6-player jokers-OFF is not a supported configuration (RULES v1.2 §2 + §9 A3). Use { players: 6, jokers: true }.",
    path: ["jokers"],
  });
export type GameConfig = z.infer<typeof GameConfigSchema>;

export const RoundConfigSchema = z.object({
  totalTricks: z.number().int().positive(),
  cardsPerPlayer: z.number().int().positive(),
  baseBid: z.number().int().positive(),
  initialDealCount: z.number().int().positive(),
});
export type RoundConfig = z.infer<typeof RoundConfigSchema>;

export const BidSchema = z.object({
  value: z.number().int().positive(),
  trump: SuitSchema,
});
export type Bid = z.infer<typeof BidSchema>;

export const PublicBiddingSchema = z.object({
  callerSeat: z.number().int().min(0),
  highBid: BidSchema.nullable(),
  highBidder: z.number().int().min(0).nullable(),
  /** v1.1 (RULES §4.2): true once the caller's forced opening has landed.
   * v1.2 keeps this — the open sub-phase is unchanged in shape, only its
   * exit conditions changed (timer OR all-non-high-bidders-passed). */
  openingResolved: z.boolean(),
  toAct: z.number().int().min(0).nullable(),
  resolved: z.boolean(),
  contractTeam: TeamSchema.nullable(),
  /** v1.1: server-authoritative wall-clock deadline (epoch ms) at which the
   * auction will end if no further bid arrives. Set when `openingResolved`
   * becomes true, reset to now+10000 on every successful bid (v1.2
   * tightened from 15000), cleared when resolved. Passes do NOT reset it.
   * Null during the opening sub-phase and after resolution. */
  auctionEndsAt: z.number().int().nullable(),
  /** v1.2 (RULES §4.2): per-seat permanent pass flags for this auction
   * window. Length always equals `config.players`. Once `passed[s] = true`
   * the server rejects further `bid` and `pass` moves from seat `s` with
   * `ALREADY_PASSED` until the round advances. */
  passed: z.array(z.boolean()),
  /** v1.4 Phase 16 (smart bots): the round's opening contract (caller's
   * forced opening). Set the moment the caller bids; mirror of the
   * engine's `BiddingState.openingBid`. Persists across open-phase
   * raises — `highBid` moves, `openingBid` does not. Bots read this to
   * anchor a hand-strength prior; the human UI does NOT render it.
   * Nullable with `.default(null)` so a v2 server that doesn't emit the
   * field parses cleanly on a v3 client (default fills undefined →
   * null). The output type stays `Bid | null` (required, no undefined)
   * because `.default()` strips undefined from the output. */
  openingBid: BidSchema.nullable().default(null),
  /** v1.4 Phase 20 (bidding skip): the current high-bidder has signaled
   * ready-to-end-the-auction. Combined with every non-high-bidder
   * having passed, the engine ends the auction synchronously without
   * waiting for the 10-s timer. False by default; resets on every bid
   * raise. `.default(false)` lets a v3 server (no field) parse cleanly
   * on a v4 client. */
  highBidderReady: z.boolean().default(false),
});
export type PublicBidding = z.infer<typeof PublicBiddingSchema>;

export const TrickPlaySchema = z.object({
  seat: z.number().int().min(0),
  card: CardSchema,
});
export type TrickPlay = z.infer<typeof TrickPlaySchema>;

export const TrickSchema = z.object({
  leaderSeat: z.number().int().min(0),
  ledSuit: SuitSchema.nullable(),
  plays: z.array(TrickPlaySchema),
});
export type Trick = z.infer<typeof TrickSchema>;

export const RoundResultSchema = z.object({
  round: z.number().int().min(0),
  contractTeam: TeamSchema,
  bid: z.number().int().positive(),
  trump: SuitSchema,
  tricks: z.record(TeamSchema, z.number().int().min(0)),
  winner: TeamSchema.nullable(),
  delta: z.record(TeamSchema, z.number().int()),
  forfeited: z
    .object({
      strandedSeat: z.number().int().min(0),
      count: z.number().int().min(0),
    })
    .optional(),
});
export type RoundResult = z.infer<typeof RoundResultSchema>;

/** Wire Move types (client never sends `seat` — server fills it from
 * connection). v1.2 re-adds `pass` (RULES §4.2 explicit pass) alongside
 * `bid`/`play`. `endAuction` is server-only and is never on the wire.
 * v1.4 Phase 20 adds `toggleReady` (high-bidder consent to skip 10-s
 * timer). */
export const WireMoveSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("bid"), value: z.number().int().min(0), trump: SuitSchema }),
  z.object({ type: z.literal("pass") }),
  z.object({ type: z.literal("toggleReady") }),
  z.object({ type: z.literal("play"), cardId: z.string() }),
]);
export type WireMove = z.infer<typeof WireMoveSchema>;

/** Full Move shape as delivered in RedactedView.legalMoves (includes seat).
 * v1.2 (RULES §4.2): adds a `pass` variant alongside `bid`/`play`.
 * v1.4 Phase 20: adds `toggleReady`. */
export const MoveSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("bid"),
    seat: z.number().int().min(0),
    value: z.number().int().min(0),
    trump: SuitSchema,
  }),
  z.object({
    type: z.literal("pass"),
    seat: z.number().int().min(0),
  }),
  z.object({
    type: z.literal("toggleReady"),
    seat: z.number().int().min(0),
  }),
  z.object({
    type: z.literal("play"),
    seat: z.number().int().min(0),
    cardId: z.string(),
  }),
]);
export type Move = z.infer<typeof MoveSchema>;

/** Structural mirror of engine RedactedView — the only game state a client ever receives. */
export const RedactedViewSchema = z.object({
  you: z.number().int().min(0),
  config: GameConfigSchema,
  rc: RoundConfigSchema,
  phase: PhaseSchema,
  round: z.number().int().min(0),
  callerSeat: z.number().int().min(0),
  yourHand: z.array(CardSchema),
  handCounts: z.array(z.number().int().min(0)),
  trump: SuitSchema.nullable(),
  bidding: PublicBiddingSchema,
  trick: TrickSchema.nullable(),
  /** v1.1 (UX): the just-completed trick for the 5-s gap between trick-end
   * and the next lead. See engine/src/game.ts redactFor for the derivation. */
  lastTrick: TrickSchema.nullable(),
  /** v1.2 Phase 13 (RULES §5.5): server-authoritative wall-clock deadline
   * (epoch ms) at which the inter-trick lingering window will close. Set on
   * every `trickWon` event that does NOT also end the round; cleared the
   * instant the next leader plays OR when the 5-s window elapses (server
   * `room.ts onAlarm`). The client gates `lastTrick` rendering on
   * `nextTrickAt !== null`. Optional + nullable so v1 servers that don't
   * emit the field parse cleanly; null and absent are equivalent. */
  nextTrickAt: z.number().int().nullable().optional(),
  tricksWon: z.record(TeamSchema, z.number().int().min(0)),
  ledger: z.array(RoundResultSchema),
  removedTwos: z.array(CardSchema),
  legalMoves: z.array(MoveSchema),
  yourTurn: z.boolean(),
  gameOver: z.boolean(),
  gameWinner: z.union([TeamSchema, z.literal("DRAW")]).nullable(),
  /** v1.2 Phase 14 (round-end / game-end Continue gate, 2026-05-21): when
   * non-null, the server has paused on a per-player consensus screen and
   * the synthetic paused view hides `yourHand` + zeros `handCounts` for
   * anti-leak. "round" = round summary screen (engine has already advanced
   * internally to the next round's bidding; the server suppresses that
   * broadcast until all humans ack). "game" = game-over screen (engine is
   * at `gameOver === true`). Optional + nullable so a v2 server that
   * doesn't emit the gate parses cleanly; null/undefined are equivalent
   * and mean "no gate active". */
  gateKind: z.union([z.literal("round"), z.literal("game")]).nullable().optional(),
  /** v1.2 Phase 14: seat indices that have already acked the active gate.
   * Bots auto-ack on gate entry; humans ack via `{ t: "continueRound" }`.
   * The client renders a "Waiting for N more…" hint from this. Default
   * `[]` when absent. */
  roundAcks: z.array(z.number().int().min(0)).optional(),
  /** v1.2 Phase 14: convenience boolean — whether THIS seat (`you`) has
   * acked the active gate. Derivable from `roundAcks.includes(you)` but
   * the server injects it pre-broadcast so the client can render the
   * Continue button's disabled state with a single read. Default `false`
   * when absent. */
  youAcked: z.boolean().optional(),
  /** v1.4 Phase 16 (smart bots): every completed trick this round, in
   * order, with the seat that won it (per RULES §5.3 resolution). Bots
   * consume this to infer voids, played-card sets, and "is the A still
   * alive?" predicates. The human play surface does NOT render this —
   * humans play from memory (LastTrickPeek was removed in Phase 16).
   * Defaults to `[]` so a v2 server's RedactedView parses cleanly on
   * a v3 client. Anti-cheat note: every play in `publicHistory` is
   * already public information (it played out in front of all seats);
   * this field just consolidates per-round trick history into one
   * structured wire field. */
  publicHistory: z
    .array(
      z.object({
        trick: TrickSchema,
        winnerSeat: z.number().int().min(0),
      }),
    )
    .default([]),
  /** v1.4 Phase 16: convenience mirror of `bidding.openingBid` at the
   * RedactedView root. Same value as `bidding.openingBid` either path;
   * this lets a bot reach the opening floor without descending into
   * `.bidding`. `.default(null)` so a v2 server (no field) parses
   * cleanly on a v3 client; output type stays `Bid | null` because
   * `.default()` strips undefined from the output. */
  openingBid: BidSchema.nullable().default(null),
  /** v1.4.1 Phase 22.1 (rematch flow): VOLATILE per-room series tally —
   * count of games each team has won within this room. Not persisted
   * (resets on server restart, owner decision #5). Optional so pre-22.1
   * fixtures and pre-22.1 server views parse. */
  seriesScore: z.record(TeamSchema, z.number()).optional(),
});
export type RedactedView = z.infer<typeof RedactedViewSchema>;

// ---------------------------------------------------------------------------
// Client → Server messages
// ---------------------------------------------------------------------------

/** v1.4.1 (Phase 21.3): display-name character allowlist. Unicode letters
 * (`\p{L}`), numbers (`\p{N}`), punctuation (`\p{P}`), and whitespace
 * (`\s`) only. Forbids:
 *   - C0/C1 control characters (Cc) like NUL, BEL, ESC.
 *   - Format characters (Cf) like zero-width space (U+200B), zero-width
 *     joiner (U+200D), and the RTL/LTR override family (U+202D, U+202E,
 *     U+2066…U+2069) that can spoof seat ownership in the lobby UI.
 *   - Surrogate halves (Cs) and unassigned codepoints (Cn).
 * Used by JoinMsg.name (server-enforced at the wire parse) AND by
 * RoomGate.tsx client-side so the host sees inline feedback before
 * pressing Join. The server still re-validates because the wire layer
 * is the only authority — see room.ts handleJoin. */
export const DISPLAY_NAME_REGEX = /^[\p{L}\p{N}\p{P}\s]+$/u;

/** v1.4.1 (Phase 21.2): optional `protocolVersion` lets the server detect
 * client/server skew at handshake time. When present and mismatched, the
 * server responds with `{ t: "error", code: "PROTOCOL_MISMATCH", ... }`
 * and closes the connection (see room.ts handleJoin). Omitted for one
 * phase to absorb deploy-rollover (Cloudflare Pages vs PartyKit deploy
 * independently); becomes required in a follow-up phase (Phase 22.5+).
 *
 * v1.4.1 (Phase 21.3): the `name` field now enforces `DISPLAY_NAME_REGEX`
 * to keep zero-width / RTL-override / control-char spoofing out of seat
 * ownership rendering. Existing v1 names that match `^[A-Za-z0-9 …]+$`
 * are a strict subset of the new allowlist — no regression. The server
 * additionally trims the parsed name before storing it on the seat
 * (display normalization). */
const JoinMsgSchema = z.object({
  t: z.literal("join"),
  code: z.string().min(1),
  name: z.string().min(1).max(32).regex(DISPLAY_NAME_REGEX),
  reclaimToken: z.string().optional(),
  protocolVersion: z.number().int().positive().optional(),
});

const SetConfigMsgSchema = z.object({
  t: z.literal("setConfig"),
  players: PlayerCountSchema,
  jokers: z.boolean(),
  teamMode: z.enum(["manual", "random"]),
});

const AssignSeatMsgSchema = z.object({
  t: z.literal("assignSeat"),
  seatIndex: z.number().int().min(0).max(5),
  playerId: z.string().min(1),
});

const StartMsgSchema = z.object({
  t: z.literal("start"),
});

const BidMsgSchema = z.object({
  t: z.literal("bid"),
  value: z.number().int().min(0),
  trump: SuitSchema,
});

/** v1.2 (RULES §4.2): explicit pass during the open sub-phase. No payload —
 * the seat is server-derived from the authenticated connection, identical
 * to bid/playCard. Rejected by the engine if the seat is the current high
 * bidder, has already passed, or the open sub-phase is not active. */
const PassMsgSchema = z.object({
  t: z.literal("pass"),
});

/** v1.4 Phase 20 (bidding skip): high-bidder toggles ready-to-end-auction.
 * No payload — server-derived seat as with bid/pass/play. Engine rejects
 * unless the seat is the current high bidder and the open sub-phase is
 * active. When the toggle results in `highBidderReady === true` AND every
 * non-high-bidder has passed, the engine ends the auction synchronously
 * without waiting for the 10-s timer. */
const ToggleReadyMsgSchema = z.object({
  t: z.literal("toggleReady"),
});

const PlayCardMsgSchema = z.object({
  t: z.literal("playCard"),
  cardId: z.string().min(1),
});

const HeartbeatMsgSchema = z.object({
  t: z.literal("heartbeat"),
});

/** v1.2 Phase 14: per-player ack of the round-end / game-end Continue gate.
 * The server matches the authenticated connection → seat (server-derived,
 * never wire-derived — same anti-cheat construction as bid/pass/play) and
 * adds the seat to `roundAcks`. Once `roundAcks` ⊇ all human seats the
 * server advances (clearing the gate broadcast for the round case, or
 * returning to lobby for the game case). Bots auto-ack on gate entry. */
const ContinueRoundMsgSchema = z.object({
  t: z.literal("continueRound"),
});

/** v1.4.1 Phase 22.1 (rematch flow): host-only request to start a new game
 * directly from the GameOver screen without round-tripping through the
 * lobby. Server enforces (a) sender is the lobby host, (b) the room is
 * currently in the `game_over` Continue gate. `resetType` controls partner
 * pairing for the next game — `same_seats` keeps the seating identical;
 * `shuffle_partners` rotates seats {1,2} (4p) / {1,2}+{3,4} (6p) so the
 * partner pairings change. Server derives the host seat from the
 * authenticated connection — wire payload carries no seat (same
 * anti-cheat construction as bid/pass/play/continueRound).
 *
 * PROTOCOL_VERSION is intentionally NOT bumped — this is a purely additive
 * new client message. A v4-only server (the current production version)
 * rejects the unknown `{ t: "rematch", ... }` at the zod parse layer with
 * `BAD_MESSAGE`, identical to how a v2 server would have rejected
 * `{ t: "toggleReady" }` before v1.4 Phase 20. The client treats
 * BAD_MESSAGE on `rematch` as "server too old — full disconnect required",
 * matching existing terminal-error UX. No RedactedView delta. */
const RematchMsgSchema = z.object({
  t: z.literal("rematch"),
  resetType: z.enum(["same_seats", "shuffle_partners"]),
});

/** Discriminated union of all Client → Server messages. */
export const ClientMessageSchema = z.discriminatedUnion("t", [
  JoinMsgSchema,
  SetConfigMsgSchema,
  AssignSeatMsgSchema,
  StartMsgSchema,
  BidMsgSchema,
  PassMsgSchema,
  ToggleReadyMsgSchema,
  PlayCardMsgSchema,
  HeartbeatMsgSchema,
  ContinueRoundMsgSchema,
  RematchMsgSchema,
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

// Individual exported types
export type JoinMsg = z.infer<typeof JoinMsgSchema>;
export type SetConfigMsg = z.infer<typeof SetConfigMsgSchema>;
export type AssignSeatMsg = z.infer<typeof AssignSeatMsgSchema>;
export type StartMsg = z.infer<typeof StartMsgSchema>;
export type BidMsg = z.infer<typeof BidMsgSchema>;
export type PassMsg = z.infer<typeof PassMsgSchema>;
export type ToggleReadyMsg = z.infer<typeof ToggleReadyMsgSchema>;
export type PlayCardMsg = z.infer<typeof PlayCardMsgSchema>;
export type HeartbeatMsg = z.infer<typeof HeartbeatMsgSchema>;
export type ContinueRoundMsg = z.infer<typeof ContinueRoundMsgSchema>;
export type RematchMsg = z.infer<typeof RematchMsgSchema>;

// ---------------------------------------------------------------------------
// Lobby shape (used in Server → Client `lobby` message)
// ---------------------------------------------------------------------------

const LobbySeatSchema = z.object({
  seatIndex: z.number().int().min(0).max(5),
  displayName: z.string(),
  kind: z.enum(["human", "bot"]),
  connected: z.boolean(),
  /** v1.4.1 Phase 22.2: true between disconnect and the GRACE_MS timeout
   * (apps/server/src/state.ts). Derived at lobby-broadcast time from
   * `seat.human && !seat.connected && disconnectedAt < GRACE_MS` — there's no
   * mutable field on SeatRec; the timestamp already encodes the lifecycle.
   * Drives the per-seat amber LED in Seat.tsx. Optional + absent on v1.4.0 and
   * earlier servers (treated as undefined → red when offline). */
  reconnecting: z.boolean().optional(),
});
export type LobbySeat = z.infer<typeof LobbySeatSchema>;

const LobbySchema = z.object({
  roomId: z.string(),
  code: z.string(),
  hostId: z.string(),
  config: z.object({
    players: PlayerCountSchema,
    jokers: z.boolean(),
    teamMode: z.enum(["manual", "random"]),
  }),
  seats: z.array(LobbySeatSchema),
  phase: z.enum(["lobby", "dealing", "bidding", "playing", "round_end", "game_over"]),
});
export type Lobby = z.infer<typeof LobbySchema>;

// ---------------------------------------------------------------------------
// GameEvent shape (mirrors engine GameEvent — structural duplication intentional)
// ---------------------------------------------------------------------------

export const GameEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("dealtInitial") }),
  z.object({ type: z.literal("twosRemoved"), cards: z.array(CardSchema) }),
  z.object({
    type: z.literal("bidPlaced"),
    seat: z.number().int().min(0),
    value: z.number().int().min(0),
    trump: SuitSchema,
  }),
  z.object({
    type: z.literal("passed"),
    seat: z.number().int().min(0),
  }),
  /** v1.4 Phase 20 (bidding skip): high-bidder's ready-to-end flag flipped.
   * `ready === true` is consent half of the new synchronous early-end;
   * `ready === false` fires automatically on every successful bid raise. */
  z.object({
    type: z.literal("highBidderReadyChanged"),
    seat: z.number().int().min(0),
    ready: z.boolean(),
  }),
  z.object({
    type: z.literal("biddingResolved"),
    contractTeam: TeamSchema,
    bid: z.number().int().positive(),
    trump: SuitSchema,
    leaderSeat: z.number().int().min(0),
  }),
  z.object({ type: z.literal("dealtRest") }),
  z.object({
    type: z.literal("cardPlayed"),
    seat: z.number().int().min(0),
    card: CardSchema,
  }),
  z.object({
    type: z.literal("trickWon"),
    seat: z.number().int().min(0),
    team: TeamSchema,
  }),
  z.object({ type: z.literal("roundScored"), result: RoundResultSchema }),
  z.object({
    type: z.literal("roundForfeited"),
    strandedSeat: z.number().int().min(0),
    count: z.number().int().min(0),
  }),
  z.object({
    type: z.literal("gameOver"),
    winner: z.union([TeamSchema, z.literal("DRAW")]),
  }),
  // Server-generated room events (not in engine but part of wire protocol)
  z.object({
    type: z.literal("botTookOver"),
    seat: z.number().int().min(0),
  }),
  z.object({
    type: z.literal("playerReclaimed"),
    seat: z.number().int().min(0),
  }),
]);
export type GameEventWire = z.infer<typeof GameEventSchema>;

// ---------------------------------------------------------------------------
// Server → Client messages
// ---------------------------------------------------------------------------

const YouAreMsgSchema = z.object({
  t: z.literal("youAre"),
  seatIndex: z.number().int().min(0).max(5),
  reclaimToken: z.string(),
});

const LobbyMsgSchema = z.object({
  t: z.literal("lobby"),
  lobby: LobbySchema,
});

const StateMsgSchema = z.object({
  t: z.literal("state"),
  view: RedactedViewSchema,
});

const EventMsgSchema = z.object({
  t: z.literal("event"),
  event: GameEventSchema,
});

const ErrorMsgSchema = z.object({
  t: z.literal("error"),
  code: z.string(),
  message: z.string(),
});

/** Discriminated union of all Server → Client messages. */
export const ServerMessageSchema = z.discriminatedUnion("t", [
  YouAreMsgSchema,
  LobbyMsgSchema,
  StateMsgSchema,
  EventMsgSchema,
  ErrorMsgSchema,
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

// Individual exported types
export type YouAreMsg = z.infer<typeof YouAreMsgSchema>;
export type LobbyMsg = z.infer<typeof LobbyMsgSchema>;
export type StateMsg = z.infer<typeof StateMsgSchema>;
export type EventMsg = z.infer<typeof EventMsgSchema>;
export type ErrorMsg = z.infer<typeof ErrorMsgSchema>;

// ---------------------------------------------------------------------------
// Parse helpers — safe-parse returning typed result
// ---------------------------------------------------------------------------

export type ParseOk<T> = { ok: true; data: T };
export type ParseErr = { ok: false; error: z.ZodError };
export type ParseResult<T> = ParseOk<T> | ParseErr;

function safeWrap<T>(result: z.SafeParseReturnType<unknown, T>): ParseResult<T> {
  if (result.success) return { ok: true, data: result.data };
  return { ok: false, error: result.error };
}

/** Parse an incoming JSON-decoded value as a ClientMessage. */
export function parseClientMessage(raw: unknown): ParseResult<ClientMessage> {
  return safeWrap(ClientMessageSchema.safeParse(raw));
}

/** Parse an incoming JSON-decoded value as a ServerMessage. */
export function parseServerMessage(raw: unknown): ParseResult<ServerMessage> {
  return safeWrap(ServerMessageSchema.safeParse(raw));
}
